/**
 * The visitor's cookie choice: what it is, how it is stored, when it expires.
 * Pure (no window, document or storage), so the prerender and tests can use it.
 *
 * Two categories: necessary (always on, not asked) and analytics (Google
 * Analytics 4 and PostHog, off until the visitor says yes). The choice lives
 * in the first-party cookie `zettl_consent`, itself necessary: it only
 * remembers the answer. On zettl.cz and app.zettl.cz it is set for `.zettl.cz`,
 * so the site and the app ask once between them.
 */

/** Raise when the categories or providers change: everyone is asked again. */
export const CONSENT_VERSION = 1;
export const CONSENT_COOKIE = "zettl_consent";
/** Ask again after six months, whatever the answer was. */
export const CONSENT_MAX_AGE_SECONDS = 182 * 24 * 60 * 60;

export interface ConsentChoice {
  analytics: boolean;
}

export interface ConsentRecord extends ConsentChoice {
  version: number;
  /** When the visitor chose, in seconds since the epoch. */
  decidedAt: number;
}

/** `1.a1.1761400000`: version, analytics (a0/a1), decision time in seconds. */
export function serializeConsent(record: ConsentRecord): string {
  return `${record.version}.a${record.analytics ? 1 : 0}.${record.decidedAt}`;
}

/**
 * A stored choice that still counts, or `null` when the visitor must be asked:
 * nothing stored, another version, malformed, from the future, or too old.
 */
export function parseConsent(
  raw: string | null | undefined,
  nowSeconds: number
): ConsentRecord | null {
  const match = raw?.match(/^(\d+)\.a([01])\.(\d+)$/);
  if (!match) return null;
  const record: ConsentRecord = {
    version: Number(match[1]),
    analytics: match[2] === "1",
    decidedAt: Number(match[3]),
  };
  if (record.version !== CONSENT_VERSION) return null;
  if (record.decidedAt > nowSeconds + 60) return null;
  if (nowSeconds - record.decidedAt >= CONSENT_MAX_AGE_SECONDS) return null;
  return record;
}

/** The value of one cookie in a `document.cookie` string. */
export function readCookie(cookies: string, name: string): string | null {
  for (const part of cookies.split(";")) {
    const [key, ...rest] = part.trim().split("=");
    if (key === name) return rest.join("=");
  }
  return null;
}

/** The production hosts that share one choice through a `.zettl.cz` cookie. */
const SHARED_HOSTS = new Set(["zettl.cz", "www.zettl.cz", "app.zettl.cz"]);

export function consentCookieDomain(host: string): string | null {
  return SHARED_HOSTS.has(host) ? ".zettl.cz" : null;
}

/** The `document.cookie` assignment that stores a choice for this host. */
export function consentCookie(record: ConsentRecord, host: string, secure: boolean): string {
  const domain = consentCookieDomain(host);
  return [
    `${CONSENT_COOKIE}=${serializeConsent(record)}`,
    "path=/",
    `max-age=${CONSENT_MAX_AGE_SECONDS}`,
    "samesite=lax",
    ...(domain ? [`domain=${domain}`] : []),
    ...(secure ? ["secure"] : []),
  ].join("; ");
}

/** Cookies the analytics tools set; removed when consent is refused or withdrawn. */
export const ANALYTICS_COOKIE =
  /^(_ga|_ga_[A-Za-z0-9]+|_gid|_gat(_.*)?|ph_.+_posthog|__ph_opt_in_out_.+)$/;

/** Every domain a cookie for this host may have been set on: none, the host, each parent. */
export function cookieDomainsFor(host: string): (string | null)[] {
  const parts = host.split(".");
  const domains: (string | null)[] = [null];
  if (parts.length < 2 || /^[\d.]+$/.test(host)) return domains;
  for (let i = 0; i < parts.length - 1; i++) {
    const domain = parts.slice(i).join(".");
    domains.push(domain, `.${domain}`);
  }
  return domains;
}
